import type { ReactNode } from 'react';
import { ActionDialog } from './ActionDialog';
import { AccountPicker } from './AccountPicker';
import { PlanPicker, usePayerPlan, type PlanChoice } from './PlanPicker';
import type { AccountHit, ActionState, EnterpriseQuote, TakeOnInput } from './pb-types';
import { channelWord, dollars, intervalWord, planWord } from './pb-words';

export interface TakeOnDialogProps extends ActionState {
  company: string;
  quotes: EnterpriseQuote[];
  search: (q: string) => Promise<AccountHit[]>;
  today: string;
  onConfirm: (input: TakeOnInput) => void;
  onClose: () => void;
}

/** "Partner pays for it": staff take the company on for its linked partner, as the partner's portal does (the same refusals). */
export function TakeOnDialog(p: TakeOnDialogProps): ReactNode {
  const pp = usePayerPlan(p.quotes, p.today);
  return (
    <ActionDialog
      {...p}
      size="lg"
      title={`A partner pays for ${p.company}`}
      confirmLabel="Partner pays for it"
      ready={pp.ready}
      consequence={<TakeOnConsequence payer={pp.payer} plan={pp.plan} quote={pp.quote} />}
      onConfirm={(reason) =>
        pp.payer && p.onConfirm({ zohoAccountId: pp.payer.zohoAccountId, ...pp.plan, reason })
      }
    >
      <AccountPicker
        label="Paying account"
        value={pp.payer}
        onChange={pp.setPayer}
        search={p.search}
      />
      <PlanPicker value={pp.plan} onChange={pp.setPlan} quotes={pp.quotes} />
    </ActionDialog>
  );
}

function TakeOnConsequence(p: {
  payer: AccountHit | null;
  plan: PlanChoice;
  quote: EnterpriseQuote | null;
}) {
  if (!p.payer)
    return (
      <p>Find the paying account: the partner, or the distributor that pays for its dealers.</p>
    );
  const name = p.payer.name;
  const what =
    p.plan.package === 'enterprise'
      ? p.quote?.pricePerPeriod != null
        ? `Enterprise at the quote's ${dollars(p.quote.pricePerPeriod)} a ${p.quote.interval}`
        : 'Enterprise on a quote'
      : `${planWord(p.plan.package)}, ${intervalWord(p.plan.interval)}, at ${name}'s partner rate`;
  return (
    <>
      <p>
        {name} pays for the company from today: {what}. Its admins see that {name} manages its plan,
        and no price.
      </p>
      <p>
        {p.payer.invoiceChannel === 'quickbooks'
          ? `${channelWord.quickbooks}: the first period, today to the day before its renewal, goes on ${name}'s open statement, billed in advance.`
          : `${channelWord.stripe}: an item on ${name}'s subscription, invoiced by Stripe.`}
      </p>
    </>
  );
}
